import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';

import { Icon } from '../components/Icon.js';
import { fitWaveform, formatVoiceTime, MAX_VOICE_NOTE_MS } from './voiceNote.js';
import styles from './VoicePlayer.module.css';

const BARS = 40;
const WIDE_BARS = 100;
const STEP_S = 5;
const LIVE_BARS = 48;
const LIVE_SAMPLE_MS = 100;

let playing: HTMLAudioElement | null = null;

interface VoicePlayerProps {
  /** Absent while a bucket link is still resolving. */
  src?: string;
  /** From the sender: a recorded WebM reports `Infinity` until fully played. */
  durationS: number;
  waveform: number[];
  onPlay?: () => void;
  onError?: () => void;
  /** Fills its row with the time beside the button, as in the composer's review. */
  wide?: boolean;
}

export function VoicePlayer({
  src,
  durationS,
  waveform,
  onPlay,
  onError,
  wide = false,
}: VoicePlayerProps) {
  const audio = useRef<HTMLAudioElement>(null);
  const [isPlaying, setPlaying] = useState(false);
  const [positionS, setPositionS] = useState(0);
  const [mediaS, setMediaS] = useState(0);
  const bars = fitWaveform(waveform, wide ? WIDE_BARS : BARS);
  const totalS = durationS > 0 ? durationS : mediaS;
  const progress = totalS > 0 ? Math.min(positionS / totalS, 1) : 0;

  useEffect(() => {
    const el = audio.current;
    return () => {
      if (playing === el) playing = null;
    };
  }, []);

  const toggle = () => {
    const el = audio.current;
    if (!el || !src) return;
    if (!el.paused) return el.pause();
    if (playing && playing !== el) playing.pause();
    playing = el;
    el.play().catch(() => setPlaying(false));
  };

  const seek = (toS: number) => {
    const el = audio.current;
    if (!el || !src || totalS <= 0) return;
    el.currentTime = Math.min(Math.max(toS, 0), totalS);
    setPositionS(el.currentTime);
  };

  const onBarsClick = (event: MouseEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    seek(((event.clientX - box.left) / box.width) * totalS);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowLeft: -STEP_S, ArrowRight: STEP_S }[event.key];
    if (step === undefined) return;
    event.preventDefault();
    seek(positionS + step);
  };

  const started = isPlaying || positionS > 0;
  const clock = (
    <span className={styles.clock}>{formatVoiceTime((started ? positionS : totalS) * 1000)}</span>
  );
  return (
    <div className={wide ? styles.playerWide : styles.player}>
      <audio
        ref={audio}
        src={src}
        preload="none"
        onPlay={() => {
          setPlaying(true);
          onPlay?.();
        }}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(event) => setPositionS(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => {
          const { duration } = event.currentTarget;
          if (Number.isFinite(duration)) setMediaS(duration);
        }}
        onEnded={(event) => {
          event.currentTarget.currentTime = 0;
          setPositionS(0);
        }}
        onError={onError}
      />
      <button
        type="button"
        className={isPlaying ? styles.playActive : styles.play}
        onClick={toggle}
        disabled={!src}
        aria-label={isPlaying ? 'Pause voice note' : 'Play voice note'}
      >
        <Icon name={isPlaying ? 'pause' : 'play'} size={12} />
      </button>
      {wide && clock}
      <div
        className={styles.bars}
        role="slider"
        tabIndex={0}
        aria-label="Position"
        aria-valuemin={0}
        aria-valuemax={Math.round(totalS)}
        aria-valuenow={Math.round(positionS)}
        aria-valuetext={`${formatVoiceTime(positionS * 1000)} of ${formatVoiceTime(totalS * 1000)}`}
        onClick={onBarsClick}
        onKeyDown={onKeyDown}
      >
        {bars.map((level, i) => (
          <span
            key={i}
            className={(i + 0.5) / bars.length <= progress ? styles.barPlayed : styles.bar}
            style={{ height: `${Math.max(level, 0.1) * 100}%` }}
          />
        ))}
      </div>
      {!wide && clock}
    </div>
  );
}

/** The mic's loudness every 100 ms, oldest first, so the newest bar enters on the right. */
function useLiveLevels(stream: MediaStream | null): number[] {
  const [levels, setLevels] = useState<number[]>(() => Array<number>(LIVE_BARS).fill(0));
  useEffect(() => {
    if (!stream || typeof AudioContext === 'undefined') return;
    const context = new AudioContext();
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    context.createMediaStreamSource(stream).connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);
    const timer = window.setInterval(() => {
      analyser.getByteTimeDomainData(samples);
      let peak = 0;
      for (const sample of samples) peak = Math.max(peak, Math.abs(sample - 128));
      setLevels((previous) => [...previous.slice(1), Math.min(peak / 64, 1)]);
    }, LIVE_SAMPLE_MS);
    return () => {
      window.clearInterval(timer);
      void context.close();
    };
  }, [stream]);
  return levels;
}

export function RecordingIndicator({
  elapsedMs,
  stream,
}: {
  elapsedMs: number;
  stream: MediaStream | null;
}) {
  const levels = useLiveLevels(stream);
  return (
    <div className={styles.recording} role="status" aria-label="Recording">
      <span className={styles.clock}>
        {formatVoiceTime(elapsedMs)}
        <span className={styles.limit}> / {formatVoiceTime(MAX_VOICE_NOTE_MS)}</span>
      </span>
      <div className={styles.liveBars} aria-hidden="true">
        {levels.map((level, i) => (
          <span
            key={i}
            className={styles.liveBar}
            style={{ height: `${Math.max(level, 0.08) * 100}%` }}
          />
        ))}
      </div>
    </div>
  );
}
